import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef, ValueGetterParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { ModuleRegistry } from '@ag-grid-community/core/dist/esm/es6/modules/moduleRegistry';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model/dist/esm/es6/serverSideRowModelModule';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter/dist/esm/es6/setFilterModule';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar/dist/esm/es6/statusBarModule';

import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, switchMap } from 'rxjs';
import { BaseGrid } from 'shared/abstract/base-grid';
import { DateFloatingFilterComponent } from 'shared/components/ag-grid/filters/date-floating-filter/date-floating-filter.component';
import { DropdownFilterValue } from 'shared/components/ag-grid/filters/dropdown-floating-filter/dropdown-floating-filter.component';
import { UserStatusEnum } from 'shared/constants/user-status-enum';
import { ColoredPillTypesEnum } from 'shared/enums/colored-pill-types.enum';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { AccessTypeEnum, PermissionAccessZoneEnum } from 'shared/interfaces/permissions.interface';
import { EventBusService } from 'shared/services/event-bus.service';
import { ModalService } from 'shared/services/modal.service';
import { ToastService } from 'shared/services/toast.service';
import { UserApiService } from 'shared/services/userApi.service';
import { supplierUserHasAccess } from 'shared/util/supplierUserHasAccess';
import { User } from 'users/interfaces/user.interface';
import { SupplierUsersService } from 'users/services/supplier-users.service';
import { SupplierUserEditComponent } from '../edit/supplier-user-edit/supplier-user-edit.component';
import { SupplierUserInviteComponent } from '../supplier-user-invite/supplier-user-invite.component';

@Component({
    selector: 'app-users-table',
    templateUrl: './supplier-users-table.component.html',
    styleUrls: ['./supplier-users-table.component.scss'],
    imports: [AgGridModule, TranslateModule],
})
export class SupplierUsersTableComponent extends BaseGrid implements OnInit {
    private isAdminUser: boolean = false;

    //TODO column headers should be translated as well
    public columnDefs: ColDef[] = [
        {
            field: 'companyName',
            hide: true,
            minWidth: 180,
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'email',
            minWidth: 250,
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'firstName',
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'lastName',
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'userAccesses.userGroup.name',
            floatingFilter: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            colId: 'userGroup',
            headerName: 'User Group',
            unSortIcon: true,
            cellRenderer: this.userGroupRenderer,
        },
        {
            field: 'listingAccessGroups',
            floatingFilter: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            valueFormatter: (params) => this.accessGroupsFormatter(params),
            colId: 'accessGroups',
            headerName: 'Access Groups',
            unSortIcon: true,
            cellRenderer: 'coloredPillCellRenderer',
            cellRendererParams: {
                pillType: (params: string) => {
                    return ColoredPillTypesEnum.NEUTRAL;
                },
                isMultiPillDisplay: true,
                useFormattedValue: true,
                value: '',
            },
        },
        {
            field: 'status',
            unSortIcon: true,
            width: 160,
            valueGetter: this.statusFormatter,
            floatingFilterComponent: 'dropdownFloatingFilter',
            floatingFilter: true,
            filterParams: {
                values: Object.keys(UserStatusEnum).map((key) => {
                    return {
                        label: key.toSentenceCase(),
                        value: key,
                    } as DropdownFilterValue;
                }) as DropdownFilterValue[],
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
            cellRenderer: 'coloredPillCellRenderer',
            cellRendererParams: {
                pillType: (params: string) => {
                    const pillTypesMap = new Map();
                    pillTypesMap.set('active', ColoredPillTypesEnum.SUCCESS);
                    pillTypesMap.set('locked', ColoredPillTypesEnum.WARNING);
                    pillTypesMap.set('disabled', ColoredPillTypesEnum.NEUTRAL);

                    return pillTypesMap.get(params.toLowerCase());
                },
            },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'lastLogin',
            valueFormatter: this.dateFormatter,
            floatingFilter: true,
            width: 270,
            floatingFilterComponent: DateFloatingFilterComponent,
            floatingFilterComponentParams: { suppressFilterButton: true },
            unSortIcon: true,
        },
        {
            colId: 'actions',
            filter: false,
            resizable: false,
            sortable: false,
            minWidth: 110,
            maxWidth: 110,
            headerComponent: 'resetFiltersHeader',
            cellRenderer: 'actionButtonsCellRenderer',
            cellRendererParams: {
                buttons: [
                    {
                        clickAction: this.deleteUserButtonClickHandler.bind(this),
                        icon: 'trash',
                        computeDisabledState: this.isDeleteDisabledForRow.bind(this),
                        computeTooltip: this.computeDeleteTooltip.bind(this),
                    },
                    {
                        clickAction: this.editUserButtonClickHandler.bind(this),
                        icon: 'pen-to-square',
                        computeDisabledState: this.isEditDisabledForRow.bind(this),
                        computeTooltip: this.computeEditTooltip.bind(this),
                    },
                ],
            },
        },
    ];

    constructor(
        router: Router,
        activatedRoute: ActivatedRoute,
        destroyRef: DestroyRef,
        private usersGridService: SupplierUsersService,
        private userApiService: UserApiService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private eventBusService: EventBusService,
        private modalService: ModalService,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(usersGridService, router, activatedRoute, destroyRef);
    }

    ngOnInit(): void {
        this.userApiService
            .getLoginInformation()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((userInformation) => {
                this.isAdminUser = userInformation.isShiji;

                if (userInformation.isShiji) {
                    this.columnDefs[0].hide = false;
                    this.agGrid?.api?.setColumnVisible('companyName', true);
                } else {
                    //supplier user should have at least "Edit" permissions
                    userInformation = userInformation as CurrentSupplierUserInterface;

                    const actionsColumn = this.columnDefs.find((x) => x.colId == 'actions') as ColDef;
                    const shouldShowActions = supplierUserHasAccess(
                        userInformation.internalPermissions,
                        PermissionAccessZoneEnum.ACCESS_TO_USERS,
                        AccessTypeEnum.EDIT,
                    );

                    if (!shouldShowActions) {
                        actionsColumn.cellRenderer = null;
                        actionsColumn.maxWidth = 60;
                    }
                }
            });

        this.eventBusService
            .getObservableForEventStream('pageMainButtonAction')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
                if (data === 'users') {
                    this.openInviteSupplierUserModal();
                }
            });
    }

    public editUserButtonClickHandler(rowData: User) {
        const modalData = { ...rowData, type: 'side' };
        this.modalService
            .openModal(SupplierUserEditComponent, modalData)
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isSaved) => {
                if (isSaved) {
                    this.agGrid?.api?.refreshServerSide({ purge: true });
                }
            });
    }

    public deleteUserButtonClickHandler(rowData: User) {
        this.modalService
            .openDeleteConfirmationModal(
                this.translateService.instant('users.supplierUsers.deleteModal.body'),
                this.translateService.instant('users.supplierUsers.deleteModal.title'),
                rowData.companyName,
                rowData.email,
                this.translateService.instant('users.supplierUsers.deleteModal.entityTypeName'),
            )
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() => {
                    return this.usersGridService.deleteSupplierUser(rowData);
                }),
            )
            .subscribe(() => {
                this.toastService.showToast('users.deleteSuccess', 'success', { user: rowData.email });
                this.agGrid?.api?.refreshServerSide({ purge: true });
            });
    }

    private openInviteSupplierUserModal() {
        this.modalService
            .openModal(SupplierUserInviteComponent, { type: 'side' })
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isSaved) => {
                //We don't need to refresh the grid since the user won't appear in the table after being invited, but only after they register
                if (isSaved && !this.agGrid) {
                    this.anyRows.set(true);
                }
            });
    }

    private userGroupRenderer(params: any) {
        return params.data.userAccesses[0]?.userGroup.name ?? '';
    }

    private statusFormatter(params: ValueGetterParams) {
        return params.data.status.toString().toSentenceCase();
    }

    private accessGroupsFormatter(params: any) {
        return params.data.listingAccessGroups.map((accessGroup: any) => accessGroup.name).join(',') ?? '';
    }

    private computeDeleteTooltip(rowData: any) {
        if (!this.isAdminUser && this.userIsInAdminGroup(rowData)) {
            return 'users.supplierUsers.cannotDeleteAdminTooltip';
        }

        return 'generalTooltips.delete';
    }

    private computeEditTooltip(rowData: any) {
        return this.isEditDisabledForRow(rowData) ? 'users.supplierUsers.cannotEditAdminTooltip' : 'generalTooltips.edit';
    }

    private isDeleteDisabledForRow(rowData: any) {
        return !this.isAdminUser && this.userIsInAdminGroup(rowData);
    }

    private isEditDisabledForRow(rowData: any): boolean {
        return !this.isAdminUser && this.userIsInAdminGroup(rowData);
    }

    private userIsInAdminGroup(userRow: any) {
        return !!userRow.userAccesses.find((userAccess: any) => userAccess.userGroup.isAdmin === true);
    }
}
